import { z } from 'zod';
import { petTrainingSchema } from '../inventory/pet-consumables';
import {
  PET_MAX_EVOLUTION,
  PET_STAR_SLOTS,
  petRaritySchema,
  petStarsSchema,
} from '../inventory/pets';

/**
 * Pets on the wire (prompt 4A).
 *
 * What a pet IS and what it is currently doing. The mutations - activating and
 * evolving - live in `pet-actions.ts`, because their answers carry a hunt
 * session and the hunt contract already reports pet state from here; keeping
 * the chain a chain is what stops two zod modules from importing each other.
 *
 * Combat numbers cross as plain JSON numbers rather than decimal strings, and
 * deliberately: damage in this game is a float in the simulation (the ship's
 * is too), it is bounded by the ship's own damage, and it is DISPLAY - the
 * authoritative arithmetic happens server-side in the re-simulation. The
 * unbounded values that must stay exact - level, XP, Gold - are still strings.
 */

export const PETS_ROUTE = '/api/pets' as const;

/** How many star slots a card draws. Filled ones come from the rarity. */
export const petStarSlotsSchema = z.literal(PET_STAR_SLOTS);

export const petFormSchema = z.object({
  evolution: z.number().int().min(1).max(PET_MAX_EVOLUTION),
  /** `MK I`, `MK II`, `MK III`. */
  name: z.string().min(1),
  /** How many projectiles leave the pet per volley in this form. */
  projectiles: z.number().int().min(1),
  assetKey: z.string().min(1),
  description: z.string().min(1),
});
export type PetFormWire = z.infer<typeof petFormSchema>;

/** One owned copy of a pet. */
export const petCopySchema = z.object({
  instanceId: z.uuid(),
  /** 1..3 - the instance level, which IS the evolution. */
  evolution: z.number().int().min(1).max(PET_MAX_EVOLUTION),
  /** True for the ONE copy in the pet slot. At most one per account. */
  active: z.boolean(),
  /** True when a listing or a trade is holding it; it cannot be spent. */
  reserved: z.boolean(),
  /** Permanent training, on THIS copy. It travels with the instance. */
  training: petTrainingSchema,
  /** The appearance THIS copy is wearing. Also travels with it. */
  energyId: z.string().min(1),
  finishId: z.string().min(1),
});
export type PetCopyWire = z.infer<typeof petCopySchema>;

export const petStateSchema = z.object({
  definitionId: z.string().min(1),
  name: z.string().min(1),
  rarity: petRaritySchema,
  /** The rating. Always agrees with the rarity - they are one fact. */
  stars: petStarsSchema,
  starSlots: petStarSlotsSchema,
  maxEvolution: z.literal(PET_MAX_EVOLUTION),
  /** Every form, so the tab can describe the one after this. */
  forms: z.array(petFormSchema),
  /** Every owned copy, weakest first. Empty when the player has none. */
  copies: z.array(petCopySchema),
  /**
   * How many evolutions are available right now: pairs of copies at the same
   * evolution, below the cap. Two MK I and one MK II is one pair.
   */
  evolvablePairs: z.number().int().min(0),
});
export type PetStateWire = z.infer<typeof petStateSchema>;

/** What the active pet contributes, as the Overview reports it. */
export const petCombatSchema = z.object({
  definitionId: z.string().min(1),
  evolution: z.number().int().min(1).max(PET_MAX_EVOLUTION),
  muzzles: z.number().int().min(1),
  damagePerBullet: z.number().min(0),
  bulletsPerSecond: z.number().min(0),
  damagePerSecond: z.number().min(0),
  /** A SHARE of the session damage, never an addition to it. */
  damageDealtThisSession: z.number().min(0),
  training: petTrainingSchema,
});
export type PetCombatWire = z.infer<typeof petCombatSchema>;

export const petsResponseSchema = z.object({
  /** Every pet in the catalog, owned or not. */
  pets: z.array(petStateSchema),
  /** The instance id in the pet slot, or null when none is active. */
  activeInstanceId: z.uuid().nullable(),
  /** The version to send with the next activation. */
  loadoutVersion: z.number().int().min(0),
  /** Bumped when the meaning of the pet catalog changes. */
  contentVersion: z.number().int().min(1),
});
export type PetsResponse = z.infer<typeof petsResponseSchema>;

/**
 * The pet a hunt session is bound to, as the client needs it to render.
 *
 * Carried by every answer that authorises a session, exactly as the loadout
 * and the artifact totals are - the client simulates and draws with what the
 * server will re-simulate with, and decides none of it.
 */
export const activePetSchema = z.object({
  instanceId: z.uuid(),
  definitionId: z.string().min(1),
  /** The pet's name, so the Overview never has to fetch the catalog to say it. */
  name: z.string().min(1),
  evolution: z.number().int().min(1).max(PET_MAX_EVOLUTION),
  /** The ACTIVE form's name: `MK I`, `MK II`, `MK III`. */
  formName: z.string().min(1),
  rarity: petRaritySchema,
  stars: petStarsSchema,
  starSlots: petStarSlotsSchema,
  /** Asset id of the 2D thumbnail for the ACTIVE form. */
  assetKey: z.string().min(1),
  /** Projectiles per volley in this form; the renderer draws one emitter each. */
  projectiles: z.number().int().min(1),
  /** Permanent training, frozen with the session. */
  training: petTrainingSchema,
  /** The appearance the renderer must apply. */
  energyId: z.string().min(1),
  finishId: z.string().min(1),
});
export type ActivePetWire = z.infer<typeof activePetSchema>;
